import React, { Component } from 'react';
import styles from './Gallery.module.scss';

// Gallery item interface
interface GalleryItem {
  id: string;
  image: string;
  title: string;
  description: string;
  category: 'tables' | 'facility' | 'tournaments' | 'vip';
  featured?: boolean;
}

interface GalleryState {
  selectedCategory: string;
  selectedImage: GalleryItem | null;
}

export class Gallery extends Component<{}, GalleryState> {
  constructor(props: {}) {
    super(props);
    this.state = {
      selectedCategory: 'all',
      selectedImage: null
    };
  }

  // Gallery data
  galleryItems: GalleryItem[] = [
  {
    id: '1',
    image: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&h=600&fit=crop&crop=center',
    title: 'Professional Tournament Tables',
    description: 'Diamond Professional tables used in world championships',
    category: 'tables',
    featured: true
  },
  {
    id: '2',
    image: 'https://images.unsplash.com/photo-1589759118394-f5cfe6178fd3?w=800&h=600&fit=crop&crop=center',
    title: 'Training Area',
    description: 'Professional coaching and skill development zone',
    category: 'facility'
  },
  {
    id: '3',
    image: 'https://images.unsplash.com/photo-1609710228159-0fa9bd7c0827?w=800&h=600&fit=crop&crop=center',
    title: 'Tournament Arena',
    description: 'Competitive matches with spectator seating',
    category: 'tournaments',
    featured: true
  },
  {
    id: '4',
    image: 'https://images.unsplash.com/photo-1551632436-cbf8dd35adfa?w=800&h=600&fit=crop&crop=center',
    title: 'VIP Lounge',
    description: 'Exclusive premium gaming experience',
    category: 'vip'
  },
  {
    id: '5',
    image: 'https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?w=800&h=600&fit=crop&crop=center',
    title: 'Standard Tables',
    description: 'High-quality tables for casual play',
    category: 'tables'
  },
  {
    id: '6',
    image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop&crop=center',
    title: 'Bar & Lounge',
    description: 'Refreshments and comfortable seating area',
    category: 'facility'
  },
  {
    id: '7',
    image: 'https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?w=800&h=600&fit=crop&crop=center',
    title: 'Championship Finals',
    description: 'High-stakes tournament action',
    category: 'tournaments'
  },
  {
    id: '8',
    image: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&h=600&fit=crop&crop=center',
    title: 'Private Events',
    description: 'Special occasions and corporate events',
    category: 'vip'
  }
];

  get categories() {
    return [
      { id: 'all', label: 'All', count: this.galleryItems.length },
      { id: 'tables', label: 'Tables', count: this.galleryItems.filter(item => item.category === 'tables').length },
      { id: 'facility', label: 'Facility', count: this.galleryItems.filter(item => item.category === 'facility').length },
      { id: 'tournaments', label: 'Tournaments', count: this.galleryItems.filter(item => item.category === 'tournaments').length },
      { id: 'vip', label: 'VIP', count: this.galleryItems.filter(item => item.category === 'vip').length }
    ];
  }

  get filteredItems() {
    return this.state.selectedCategory === 'all' 
      ? this.galleryItems 
      : this.galleryItems.filter(item => item.category === this.state.selectedCategory);
  }

  get featuredItems() {
    return this.galleryItems.filter(item => item.featured);
  }

  render() {
    const { selectedCategory, selectedImage } = this.state;
    
    return (
      <section className={styles.gallery}>
        <div className={styles.container}>
          {/* Header */}
          <div className={styles.header}>
            <h2 className={styles.title}>Our Facilities</h2>
            <p className={styles.subtitle}>
              Experience the finest billiards environment with professional-grade tables and premium amenities
            </p>
          </div>

          {/* Featured Items */}
          <div className={styles.featuredSection}>
            <h3 className={styles.featuredTitle}>Featured Highlights</h3>
            <div className={styles.featuredGrid}>
              {this.featuredItems.map((item) => (
                <div 
                  key={item.id} 
                  className={styles.featuredCard}
                  onClick={() => this.setState({ selectedImage: item })}
                >
                  <div className={styles.imageContainer}>
                    <img src={item.image} alt={item.title} className={styles.image} />
                    <div className={styles.overlay}>
                      <div className={styles.overlayContent}>
                        <h4 className={styles.cardTitle}>{item.title}</h4>
                        <p className={styles.cardDescription}>{item.description}</p>
                        <span className={styles.categoryBadge}>{item.category.toUpperCase()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Category Filter */}
          <div className={styles.filterSection}>
            <div className={styles.filterTabs}>
              {this.categories.map((category) => (
                <button
                  key={category.id}
                  className={`${styles.filterTab} ${selectedCategory === category.id ? styles.active : ''}`}
                  onClick={() => this.setState({ selectedCategory: category.id })}
                >
                  {category.label}
                  <span className={styles.count}>({category.count})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Gallery Grid */}
          <div className={styles.galleryGrid}>
            {this.filteredItems.map((item, index) => (
              <div 
                key={item.id} 
                className={styles.galleryItem}
                onClick={() => this.setState({ selectedImage: item })}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={styles.imageContainer}>
                  <img src={item.image} alt={item.title} className={styles.image} />
                  <div className={styles.overlay}>
                    <div className={styles.overlayContent}>
                      <h4 className={styles.cardTitle}>{item.title}</h4>
                      <p className={styles.cardDescription}>{item.description}</p>
                      <span className={styles.categoryBadge}>{item.category.toUpperCase()}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Modal */}
          {selectedImage && (
            <div className={styles.modal} onClick={() => this.setState({ selectedImage: null })}>
              <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <button 
                  className={styles.closeButton}
                  onClick={() => this.setState({ selectedImage: null })}
                  aria-label="Close modal"
                >
                  ×
                </button>
                <img src={selectedImage.image} alt={selectedImage.title} className={styles.modalImage} />
                <div className={styles.modalInfo}>
                  <h3 className={styles.modalTitle}>{selectedImage.title}</h3>
                  <p className={styles.modalDescription}>{selectedImage.description}</p>
                  <span className={styles.modalCategory}>{selectedImage.category.toUpperCase()}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }
}
